import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './ChapterPage.css';

/** Returns the member's primary title (a position without a chapter-prefix colon). */
const getPrimaryTitle = (member) => {
  if (!member?.positions?.length) return 'Chapter Leadership';
  const general = member.positions.find((p) => !p.includes(':'));
  return general || member.positions[0];
};

const ChapterLayout = ({ chapter, leadership = [], alumni = [], gallery = [] }) => {
  const [selectedLeader, setSelectedLeader] = useState(null);

  const openBio = (member) => {
    setSelectedLeader(member);
    document.body.style.overflow = 'hidden';
  };

  const closeBio = () => {
    setSelectedLeader(null);
    document.body.style.overflow = '';
  };

  return (
    <div className="chapter-page">

      {/* Hero */}
      <header
        className="chapter-hero"
        style={{
          backgroundImage: `linear-gradient(170deg, rgba(8,12,20,0.55) 0%, rgba(8,12,20,0.25) 100%), url(${chapter.image})`,
        }}
      >
        <div className="chapter-hero__inner">
          <Link to="/chapters" className="chapter-hero__back">← Chapters</Link>
          <div className="chapter-hero__content">
            <p className="chapter-hero__badge">{chapter.established}</p>
            <h1>{chapter.name}</h1>
            <p className="chapter-hero__campus">{chapter.campus}</p>
            {chapter.focus && (
              <p className="chapter-hero__focus">{chapter.focus}</p>
            )}
          </div>
        </div>
      </header>

      {/* Photo gallery */}
      {gallery.length > 0 && (
        <section className="chapter-gallery">
          <div className="chapter-gallery__inner">
            {gallery.map((src, i) => (
              <img key={i} src={src} alt={`${chapter.name}`} loading="lazy" />
            ))}
          </div>
        </section>
      )}

      {/* Leadership grid */}
      {leadership.length > 0 && (
        <section className="chapter-section">
          <div className="chapter-section__inner">
            <div className="chapter-section__header">
              <h2>Chapter Leadership</h2>
              <p>The people running programs and supporting students on campus.</p>
            </div>
            <div className="leader-grid">
              {leadership.map((member) => (
                <button
                  key={member.name}
                  type="button"
                  className="leader-card"
                  onClick={() => openBio(member)}
                >
                  <div className="leader-card__photo">
                    <img src={member.image} alt={member.name} loading="lazy" />
                  </div>
                  <div className="leader-card__info">
                    <p className="leader-card__name">{member.name}</p>
                    <p className="leader-card__title">{getPrimaryTitle(member)}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Alumni */}
      {alumni.length > 0 && (
        <section className="chapter-section">
          <div className="chapter-section__inner">
            <div className="chapter-section__header">
              <h2>Past Leadership</h2>
              <p>Alumni who helped build the chapter's foundation.</p>
            </div>
            <div className="chapter-alumni">
              {alumni.map((alumnus) => (
                <article key={alumnus.name} className="chapter-alumni__card">
                  <p className="chapter-alumni__name">{alumnus.name}</p>
                  <ul>
                    {(alumnus.positions || []).map((role) => (
                      <li key={`${alumnus.name}-${role}`}>{role}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bio modal */}
      <div
        className={`chapter-bio-modal ${selectedLeader ? 'is-open' : ''}`}
        aria-hidden={!selectedLeader}
      >
        <div className="chapter-bio-modal__backdrop" onClick={closeBio} />
        {selectedLeader && (
          <div className="chapter-bio-modal__panel" role="dialog" aria-modal="true">
            <button className="chapter-bio-modal__close" onClick={closeBio} aria-label="Close">
              ✕
            </button>
            <div className="chapter-bio-modal__photo">
              <img src={selectedLeader.image} alt={selectedLeader.name} loading="lazy" />
            </div>
            <div className="chapter-bio-modal__body">
              <p className="chapter-bio-modal__eyebrow">{getPrimaryTitle(selectedLeader)}</p>
              <h2 className="chapter-bio-modal__name">{selectedLeader.name}</h2>
              <ul className="chapter-bio-modal__roles">
                {(selectedLeader.positions || []).map((role, i) => (
                  <li key={i}>{role}</li>
                ))}
              </ul>
              {selectedLeader.text && (
                <p className="chapter-bio-modal__text">{selectedLeader.text}</p>
              )}
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default ChapterLayout;
